import { useState, type FC } from 'react';
import { Sparkles } from 'lucide-react';
import { DROP_GEN_SENDERS_PER_DROP_MAX, senderIdProblem } from '@trafficpulse/shared';
import { useGenerateSenders } from '@/features/campaign-texts/hooks/useGenerateSenders';
import { getApiErrorMessage } from '@/lib/utils';

interface Props {
  countryKey?: string;
  groupIds: string[];
  amount: number;
  existing: string[];
  preview?: boolean;
  onApply: (names: string[]) => void;
}

/** Only reveal the review step after generation, with one action to use the names. */
export const GenerateLaneSenders: FC<Props> = ({ countryKey, groupIds, amount, existing, onApply, preview = false }) => {
  const generate = useGenerateSenders();
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const suggestions = (text ?? '').split(/[\n,]+/).map((v) => v.trim()).filter(Boolean);
  const merged = [...existing];
  for (const name of suggestions) if (!merged.some((v) => v.toUpperCase() === name.toUpperCase())) merged.push(name);
  const problem = suggestions.map(senderIdProblem).find(Boolean)
    ?? (merged.length > DROP_GEN_SENDERS_PER_DROP_MAX ? 'At most 50 sender IDs per gateway row.' : null);
  const run = async () => {
    if (!countryKey || groupIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const counts = new Map<string, number>();
      for (let i = 0; i < amount; i++) {
        const group = groupIds[i % groupIds.length];
        counts.set(group, (counts.get(group) ?? 0) + 1);
      }
      const names: string[] = [];
      for (const [groupId, n] of counts) {
        const result = await generate.mutateAsync({ countryKey, groupId, amount: n });
        names.push(...result.senders);
      }
      setText(names.filter((name, i) => names.findIndex((v) => v.toUpperCase() === name.toUpperCase()) === i).join('\n'));
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const label = busy ? 'Generating…' : text !== null ? 'Regenerate' : `${preview ? 'Preview' : 'Generate'} ${amount} sender${amount === 1 ? '' : 's'}`;
  return <div className="space-y-3">
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" disabled={busy || !countryKey || groupIds.length === 0} onClick={() => void run()} className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50">
        <Sparkles className="h-4 w-4" />{label}
      </button>
      {text !== null ? <button type="button" disabled={busy || suggestions.length === 0 || Boolean(problem)} onClick={() => onApply(merged)} className="inline-flex h-10 items-center rounded-md border border-primary px-4 text-sm font-medium text-primary disabled:opacity-50">Use these senders</button> : null}
    </div>
    {text !== null ? <label className="block text-xs font-medium">Review sender IDs
      <textarea aria-label="Generated sender IDs" rows={8} value={text} disabled={busy} onChange={(e) => setText(e.target.value)} className="mt-1.5 min-h-36 w-full rounded-md border border-border bg-background p-3 font-mono text-sm leading-6" />
    </label> : null}
    {text !== null ? <p className="text-xs text-muted-foreground">One name per line. Edit or remove any, then use them on every drop from this gateway.</p> : null}
    {error || problem ? <p role="alert" className="text-xs text-destructive">{error ?? problem}</p> : null}
  </div>;
};
